import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import { AuthContext } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../app/models/Auth';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import PageHeader from '../../../components/PageHeader/PageHeader';
import DefaultCard from '../../../components/default/DefaultCard';
import DefaultModal from '../../../components/default/DefaultModal';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import InputField from '../../../components/InputField';
import TextAreaField from '../../../components/TextAreaField';
import SelectTwo from '../../../components/SelectTwo';
import UsageBar from '../components/UsageBar';
import { LawyerPlan, Member, OrganizationData, PlanSummary, ROLE_OPTIONS, formatCLP, formatDate, statusLabel } from '../types';

interface SubscriptionRow {
    id: number;
    plan_name: string;
    payment_status: string;
    subscription_status: string | null;
    is_subscription: boolean;
    is_manual_assignment: boolean;
    assigned_by: string | null;
    paid_by: string | null;
    amount: number;
    start_date: string | null;
    end_date: string | null;
    is_active: boolean;
}

const OrganizationDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const {
        getAdminOrganization, suspendOrganization, reactivateOrganization, assignPlanToOrganization, getPublicPlans,
        inviteToOrganizationAdmin,
        fetchingAdminOrganization, fetchingAdminAction
    } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation } = useReactConfirmAlert();
    const { auth } = useContext(AuthContext);
    const canChange = hasPermission(auth, 'change_organization');

    const [organization, setOrganization] = useState<OrganizationData | null>(null);
    const [plan, setPlan] = useState<PlanSummary | null>(null);
    const [members, setMembers] = useState<Member[]>([]);
    const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
    const [plans, setPlans] = useState<LawyerPlan[]>([]);
    const [showSuspend, setShowSuspend] = useState(false);
    const [reason, setReason] = useState('');
    const [showAssign, setShowAssign] = useState(false);
    const [assign, setAssign] = useState({ plan_id: '', months: '' });
    const [showInvite, setShowInvite] = useState(false);
    const [invite, setInvite] = useState({ email: '', role: 'lawyer', first_name: '', last_name: '' });

    const load = () => {
        getAdminOrganization(Number(id), {
            onSuccess: (response) => {
                setOrganization(response.data.organization);
                setPlan(response.data.plan);
                setMembers(response.data.members);
                setSubscriptions(response.data.subscriptions);
                getPublicPlans(response.data.organization.organization_type, {
                    onSuccess: (r) => setPlans(r.data.plans)
                });
            },
            onError: (response) => toast.error(response.message)
        });
    };

    useEffect(() => {
        load();
    }, [id]);

    const handleSuspend = () => {
        suspendOrganization(Number(id), reason, {
            onSuccess: (response) => {
                toast.success(response.message);
                setShowSuspend(false);
                setReason('');
                setErrors({});
                load();
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => setErrors(response.data)
        });
    };

    const handleReactivate = () => {
        requestConfirmation({
            title: '¿Reactivar la organización?',
            message: 'Vuelve a poder hacer cambios y sumar clientes según su plan.',
            buttons: {
                confirmButton: {
                    label: 'Sí, reactivar',
                    onClick: () => reactivateOrganization(Number(id), {
                        onSuccess: (response) => {
                            toast.success(response.message);
                            load();
                        },
                        onError: (response) => toast.error(response.message)
                    })
                },
                cancelButton: { label: 'Cancelar' }
            }
        });
    };

    const handleAssign = () => {
        assignPlanToOrganization(Number(id), { plan_id: assign.plan_id, months: assign.months }, {
            onSuccess: (response) => {
                toast.success(response.message);
                setShowAssign(false);
                setErrors({});
                load();
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => setErrors(response.data)
        });
    };

    const handleInvite = () => {
        inviteToOrganizationAdmin(Number(id), invite, {
            onSuccess: (response) => {
                toast.success(response.message);
                setShowInvite(false);
                setInvite({ email: '', role: 'lawyer', first_name: '', last_name: '' });
                setErrors({});
                load();
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => setErrors(response.data)
        });
    };

    if (!organization || !plan) {
        return <div className="text-center py-5">{fetchingAdminOrganization && <div className="spinner-border text-primary" />}</div>;
    }

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Estudios y Abogados', path: '/intranet/organizations' },
                    { label: organization.name, isCurrent: true }
                ]}
                title={organization.name}
                description={`${organization.organization_type_display} · ${organization.owner_name} (${organization.owner_email}) · alta ${formatDate(organization.created_at)}`}
            />

            {organization.status === 'suspended' && (
                <div className="alert alert-danger">
                    <strong>Suspendida.</strong> {organization.suspension_reason}
                </div>
            )}

            <div className="row">
                <div className="col-lg-5 mb-3">
                    <DefaultCard>
                        <h5 className="mb-1">Plan {plan.plan?.name || '—'}</h5>
                        <p className="text-muted small">
                            {plan.is_fallback ? 'Sin suscripción vigente (límites del plan gratuito).' : plan.subscription?.is_manual_assignment ? 'Asignado por Monere.' : 'Suscripción con Flow.'}
                        </p>
                        <UsageBar label="Usuarios" used={plan.usage.seats} limit={plan.limits.max_seats} />
                        <UsageBar label="Clientes" used={plan.usage.clients} limit={plan.limits.max_clients} />
                        <UsageBar label="Marcas en curso" used={plan.usage.brand_applications} limit={plan.limits.max_brand_applications} />
                        {canChange && <div className="d-flex flex-wrap gap-2 mt-3">
                            <ButtonGeneral onClick={() => { setErrors({}); setShowAssign(true); }} title="Asignar plan" icon="fa-gift" size="small" />
                            {organization.status === 'active' && (
                                <ButtonGeneral onClick={() => { setErrors({}); setShowInvite(true); }} title="Invitar al estudio"
                                               variant="outline" size="small" icon="fa-user-plus" />
                            )}
                            {organization.status === 'active' ? (
                                <ButtonGeneral onClick={() => { setErrors({}); setShowSuspend(true); }} title="Suspender" variant="danger" size="small" icon="fa-ban" />
                            ) : (
                                <ButtonGeneral onClick={handleReactivate} title="Reactivar" variant="outline" size="small" icon="fa-undo" loading={fetchingAdminAction} />
                            )}
                        </div>}
                    </DefaultCard>
                </div>
                <div className="col-lg-7 mb-3">
                    <DefaultCard>
                        <h5 className="mb-3">Equipo ({members.length})</h5>
                        <ul className="list-group list-group-flush">
                            {members.map((member) => (
                                <li key={member.id} className="list-group-item px-0 d-flex justify-content-between">
                                    <span>{member.name} <small className="text-muted">· {member.email}</small></span>
                                    <span className="small">{member.role_display} · {member.status_display}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-muted small mt-3 mb-0">
                            <i className="fas fa-lock me-1" />Los clientes y casos del estudio no se muestran aquí: son confidenciales del estudio.
                        </p>
                    </DefaultCard>
                </div>
            </div>

            <DefaultCard>
                <h5 className="mb-3">Historial de suscripciones</h5>
                <div className="table-responsive">
                    <table className="table table-sm align-middle">
                        <thead><tr><th>Plan</th><th>Origen</th><th>Estado</th><th>Monto</th><th>Desde</th><th>Hasta</th></tr></thead>
                        <tbody>
                            {subscriptions.map((sub) => (
                                <tr key={sub.id} className={sub.is_active ? '' : 'text-muted'}>
                                    <td>{sub.plan_name}</td>
                                    <td><small>{sub.is_manual_assignment ? `Manual (${sub.assigned_by || 'Monere'})` : sub.is_subscription ? `Flow (${sub.paid_by})` : 'Gratuito'}</small></td>
                                    <td><small>{statusLabel(sub.subscription_status || sub.payment_status)}{sub.is_active ? '' : ' · cerrada'}</small></td>
                                    <td><small>{sub.amount ? formatCLP(sub.amount) : '—'}</small></td>
                                    <td><small>{formatDate(sub.start_date)}</small></td>
                                    <td><small>{sub.end_date ? formatDate(sub.end_date) : 'Sin término'}</small></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </DefaultCard>

            <DefaultModal title="Suspender organización" show={showSuspend} handleClose={() => setShowSuspend(false)}
                          actionButton={<ButtonSaveForm callbackSave={handleSuspend} loading={fetchingAdminAction} title="Suspender" />}>
                <p className="small text-muted">El estudio podrá ver sus datos pero no hacer cambios ni sumar clientes. Sus clientes siguen viendo lo suyo.</p>
                <TextAreaField label="Motivo (lo verá el estudio)" value={reason} rows={3} maxLength={500}
                               onChange={setReason} onFocus={() => onFocusRemove('reason')} error={fieldErrorMessage('reason')} />
            </DefaultModal>

            <DefaultModal title={`Invitar a ${organization.name}`} show={showInvite} handleClose={() => setShowInvite(false)}
                          actionButton={<ButtonSaveForm callbackSave={handleInvite} loading={fetchingAdminAction} title="Enviar invitación" />}>
                <p className="small text-muted">
                    La invitación sale con la marca del estudio. Si la persona ya tiene cuenta (por ejemplo, un abogado
                    que ya trabajaba con Monere) solo la acepta con su contraseña; si no, crea su cuenta al aceptar.
                    Sus casos propios pasan al estudio y los que Monere le asignó vuelven a la bandeja de Monere.
                </p>
                <div className="mb-3">
                    <InputField label="Correo" type="email" value={invite.email} required
                                onChange={(value) => setInvite({ ...invite, email: value })}
                                onFocus={() => onFocusRemove('email')} error={fieldErrorMessage('email')} />
                </div>
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <InputField label="Nombre" value={invite.first_name}
                                    onChange={(value) => setInvite({ ...invite, first_name: value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="Apellido" value={invite.last_name}
                                    onChange={(value) => setInvite({ ...invite, last_name: value })} />
                    </div>
                </div>
                <SelectTwo label="Rol" inputValue={invite.role} options={ROLE_OPTIONS}
                           onChange={(option: any) => setInvite({ ...invite, role: option?.value || 'lawyer' })}
                           hasError={!!fieldErrorMessage('role')} />
                {fieldErrorMessage('role') && <small className="text-danger">{fieldErrorMessage('role')}</small>}
            </DefaultModal>
            <DefaultModal title="Asignar plan sin cobro" show={showAssign} handleClose={() => setShowAssign(false)}
                          actionButton={<ButtonSaveForm callbackSave={handleAssign} loading={fetchingAdminAction} title="Asignar" />}>
                <div className="mb-3">
                    <SelectTwo label="Plan" inputValue={assign.plan_id}
                               options={plans.map((p) => ({ value: String(p.id), label: `${p.name} · ${p.is_free ? 'gratis' : formatCLP(p.price_with_tax)}` }))}
                               onChange={(option: any) => setAssign({ ...assign, plan_id: option?.value || '' })}
                               hasError={!!fieldErrorMessage('plan_id')} />
                    {fieldErrorMessage('plan_id') && <small className="text-danger">{fieldErrorMessage('plan_id')}</small>}
                </div>
                <InputField label="Meses (vacío = sin término)" type="number" value={assign.months} min="1" max="60"
                            onChange={(value) => setAssign({ ...assign, months: value })}
                            onFocus={() => onFocusRemove('months')} error={fieldErrorMessage('months')} />
                <small className="text-muted">Para convenios, pruebas o financiamiento CORFO. Reemplaza el plan vigente.</small>
            </DefaultModal>
        </div>
    );
};

export default OrganizationDetail;
